import { Link } from "react-router-dom";
import { BellOff, Check, FolderKanban, ListChecks, Mail, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { spaceOf } from "@/lib/spaces";
import { fmtDate, fromNow, reminderState } from "@/lib/time";
import { priorityOf, scheduleText } from "@/lib/priority";
import { SpaceAvatar } from "@/components/SpaceAvatar";
import { ItemMenu } from "@/components/Bits";
import { useSettings } from "@/context/SettingsContext";
import { sassFor } from "@/lib/fun";
import { overdueMinutes } from "@/lib/priority";

const overdueStyle = (r, mode, minutes) => {
  const mins = overdueMinutes(r);
  if (mode === "none" || !mins || mins < minutes) return {};
  if (mode === "red") return { style: { background: `rgba(239,68,68,${Math.min(0.28, 0.06 + mins / 3000)})`, borderColor: "rgba(239,68,68,0.45)" } };
  if (mode === "wobble") return { cls: "wobble" };
  return { sass: true };
};

const STATE = {
  scheduled: { label: "Scheduled", cls: "bg-blue-500/10 text-blue-300" },
  due: { label: "Sending now", cls: "bg-amber-500/15 text-amber-300" },
  sent: { label: "Sent", cls: "bg-emerald-500/10 text-emerald-300" },
  done: { label: "Done", cls: "bg-white/5 text-muted-foreground" },
};

export const ReminderItem = ({ r, showProject = true }) => {
  const { run, openEdit, openTicket } = useApp();
  const { prefs, team, celebrate } = useSettings();
  const od = overdueStyle(r, prefs.overdue_style || "red", team?.reminders?.overdue_minutes || 60);
  const st = reminderState(r);
  const done = st === "done";
  const when = r.next_fire_at || r.last_sent_at || r.due_at;
  const toggle = () => run(() => api.patch(`/reminders/${r.id}`, { status: done ? "pending" : "done" }), done ? "Reopened" : "Marked done").then((ok) => ok && !done && celebrate(r.title));
  const remove = () => run(() => api.delete(`/reminders/${r.id}`), "Reminder deleted");
  const latest = r.updates?.length ? r.updates[r.updates.length - 1] : null;

  return (
    <div data-testid={`reminder-card-${r.id}`} style={od.style} className={`group relative flex gap-4 rounded-xl border bg-background/40 p-4 pl-5 transition-colors hover:border-white/20 ${done ? "opacity-60" : ""} ${od.cls || ""}`}>
      <span className="absolute bottom-3 left-0 top-3 w-[3px] rounded-full" style={{ background: spaceOf(r.space).color }} />
      <button onClick={toggle} data-testid={`reminder-mark-done-${r.id}`} title={done ? "Reopen" : "Mark done"}
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${done ? "border-emerald-400 bg-emerald-400 text-black" : "border-white/25 hover:border-emerald-400"}`}>
        {done && <Check className="h-3 w-3" strokeWidth={3} />}
      </button>
      <div className="min-w-0 flex-1 cursor-pointer" role="button" tabIndex={0} onClick={() => openTicket(r.id)} onKeyDown={(e) => e.key === "Enter" && openTicket(r.id)} data-testid={`reminder-open-${r.id}`}>
        <p className={`font-medium leading-snug ${done ? "line-through" : ""}`} data-testid={`reminder-title-${r.id}`}>{r.title}</p>
        {r.details && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{r.details}</p>}
        {od.sass && <p className="mt-1 text-xs italic text-red-300" data-testid={`reminder-sass-${r.id}`}>{sassFor(r.id)}</p>}
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
          <span className={`rounded-full px-2 py-0.5 font-semibold ${STATE[st].cls}`} data-testid={`reminder-state-${r.id}`}>{STATE[st].label}</span>
          <span className="font-mono">{fmtDate(when)}</span>
          {!done && <span>{st === "sent" ? `sent ${fromNow(r.last_sent_at)}` : fromNow(when)}</span>}
          {done && r.completed_at && <span className="font-medium text-emerald-300/80" data-testid={`reminder-completed-${r.id}`}>Done {fmtDate(r.completed_at)}{r.completed_by ? ` by ${spaceOf(r.completed_by).name}` : ""}</span>}
          <span className="flex items-center gap-1.5" data-testid={`reminder-priority-${r.id}`}>
            <span className="h-2 w-2 rounded-full" style={{ background: priorityOf(r.priority).color }} /> {scheduleText(r)}
          </span>
          {r.sent_count > 0 && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> ×{r.sent_count}</span>}
          {r.silent && <span className="flex items-center gap-1 text-amber-200/80" data-testid={`reminder-silent-${r.id}`}><BellOff className="h-3 w-3" /> No emails</span>}
          {r.checklist?.length > 0 && <span className="flex items-center gap-1" data-testid={`reminder-checklist-${r.id}`}><ListChecks className="h-3 w-3" /> {r.checklist.filter((c) => c.done).length}/{r.checklist.length}</span>}
          {showProject && r.project_title && (
            <Link to={`/projects/${r.project_id}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 hover:text-foreground"><FolderKanban className="h-3 w-3" /> {r.project_title}</Link>
          )}
        </div>
        {latest && (
          <div className="mt-2.5 flex items-start gap-2 rounded-lg bg-white/[0.04] px-3 py-2 text-xs" data-testid={`reminder-latest-update-${r.id}`}>
            <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <p className="min-w-0 flex-1">
              <span className="font-semibold">{spaceOf(latest.created_by).name}</span> <span className="text-muted-foreground">· {fromNow(latest.created_at)}</span>
              <span className="mt-0.5 line-clamp-2 block text-foreground/90">{latest.body}</span>
            </p>
            {r.updates.length > 1 && <span className="shrink-0 rounded-full bg-white/[0.06] px-2 py-0.5 font-mono text-muted-foreground">{r.updates.length} updates</span>}
          </div>
        )}
      </div>
      <div className="flex items-start gap-1">
        <div className="flex -space-x-2" data-testid={`reminder-people-${r.id}`}>{[r.space, ...(r.assignees || [])].map((k) => <SpaceAvatar key={k} space={k} />)}</div>
        <ItemMenu testid={`reminder-${r.id}`} onEdit={() => openEdit("reminder", r)} onDelete={remove} />
      </div>
    </div>
  );
};
